import { Trans, useLingui } from "@lingui/react/macro";
import { useFormContext } from "react-hook-form";
import Form from "@/components/Form/Form";
import Fieldset from "@medoma/components/Fieldset/Fieldset";
import InputField from "@medoma/components/InputField/InputField";
import Checkbox from "@/components/Checkbox/Checkbox";
import { Text } from "@medoma/components/Text/Text";
import {
  informationFieldConfigValueSchema,
  type IInformationFieldConfigValue,
} from "@/api/Patients";
import { useUserProfile } from "@/api/Users";
import {
  localizePhoneNumber,
  type LocalizedPhoneNumber,
} from "@medoma/models/phoneNumbers";
import type { IRelative } from "@medoma/models/patients";
import styles from "./CloseContactInput.module.scss";

export type CloseContactInputFields = {
  // Nothing pre-fills a close contact, and the fieldset is left out entirely when the
  // organization has switched the field off or the patient already has close contacts, so
  // the whole group is absent until it is rendered.
  closeContact?: {
    name: string;
    phoneNumber: LocalizedPhoneNumber;
    relation: string;
    isLivingTogether: boolean;
  };
};

// A patient who already has close contacts keeps them; admission neither repeats nor edits
// them, so they are shown for reference only and further changes go through patient
// information.
export const ExistingCloseContacts = ({
  relatives,
}: {
  relatives: IRelative[];
}) => {
  const { t } = useLingui();
  const { countryCode } = useUserProfile();

  return (
    <Fieldset legend={t`Anhöriga`} visualStyle="framed">
      <ul className={styles.closeContactList}>
        {relatives.map(
          ({ id, name, phoneNumber, relation, isLivingTogether }) => {
            const lowercaseRelation = relation.toLocaleLowerCase();
            const optionalLivesTogetherSegment = isLivingTogether
              ? t`bor med patienten och`
              : "";
            const localizedPhoneNumber = phoneNumber
              ? localizePhoneNumber({ countryCode, phoneNumber })
              : null;

            return (
              <li key={id}>
                <Text element="span">
                  {localizedPhoneNumber ? (
                    <Trans>
                      {name} ({lowercaseRelation}){" "}
                      {optionalLivesTogetherSegment} nås på{" "}
                      {localizedPhoneNumber}
                    </Trans>
                  ) : (
                    <Trans>
                      {name} ({lowercaseRelation}){" "}
                      {optionalLivesTogetherSegment} saknar telefonnummer
                    </Trans>
                  )}
                </Text>
              </li>
            );
          },
        )}
      </ul>
      <Text element="p">
        <Trans>Lägg till eller ändra anhöriga under patientinformation.</Trans>
      </Text>
    </Fieldset>
  );
};

// One close contact can be added while admitting. A name and a relation identify the
// contact and the endpoint needs both, so starting anywhere in the fieldset makes those two
// required; the phone number stays optional either way.
const CloseContactInput = ({
  closeContactFieldConfig,
}: {
  closeContactFieldConfig: IInformationFieldConfigValue;
}) => {
  const { t } = useLingui();
  const {
    register,
    watch,
    formState: { errors },
  } = useFormContext<CloseContactInputFields>();

  // Undefined until the fields below register themselves.
  const closeContact = watch("closeContact");
  const isStarted = Boolean(
    closeContact?.name || closeContact?.phoneNumber || closeContact?.relation,
  );
  const isIdentityRequired =
    closeContactFieldConfig ===
      informationFieldConfigValueSchema.enum.required || isStarted;

  return (
    <Fieldset
      legend={
        closeContactFieldConfig ===
        informationFieldConfigValueSchema.enum.optional
          ? t`Anhörig (valfri)`
          : t`Anhörig`
      }
      visualStyle="framed"
    >
      <Form.Section>
        <InputField
          label={t`Namn`}
          showOptionalLabel={false}
          errorMessage={errors.closeContact?.name?.message}
          {...register("closeContact.name", {
            required: {
              value: isIdentityRequired,
              message: t`Ange namn`,
            },
          })}
        />
        <InputField
          type="tel"
          label={t`Telefonnummer`}
          showOptionalLabel
          errorMessage={errors.closeContact?.phoneNumber?.message}
          {...register("closeContact.phoneNumber", {
            pattern: {
              value: /^([0-9]|\s)+$/,
              message: t`Telefonnumret får bara innehålla siffror`,
            },
          })}
        />
        <InputField
          label={t`Relation`}
          showOptionalLabel={false}
          errorMessage={errors.closeContact?.relation?.message}
          {...register("closeContact.relation", {
            required: {
              value: isIdentityRequired,
              message: t`Ange relation`,
            },
          })}
        />
        <Checkbox
          label={{ text: t`Sammanboende` }}
          {...register("closeContact.isLivingTogether")}
        />
      </Form.Section>
    </Fieldset>
  );
};

export default CloseContactInput;
